import useRequest from "@/lib/request/useRequest";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import {
    faPlus,
    faSearch,
    faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    Chip,
    Divider,
    Input,
    Select,
    SelectItem,
} from "@heroui/react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { Organization } from "@/types/organization";
import TableV2 from "@/components/table/TableV2";

type NetworkOrganizationsSearch = {
    search?: string;
    ordering?: string;
    group_type?: string;
    service_provider?: string;
};

export const Route = createFileRoute("/dashboard/network/organizations")({
    component: RouteComponent,
    validateSearch: (
        search: Record<string, unknown>,
    ): NetworkOrganizationsSearch => {
        return {
            search: search.search as string,
            ordering: search.ordering as string,
            group_type: search.group_type as string,
            service_provider: search.service_provider as string,
        };
    },
});

function formatGroupType(type?: string) {
    if (!type) return "";
    if (type === "service provider") return "CSP";
    return type.charAt(0).toUpperCase() + type.slice(1);
}

function RouteComponent() {
    const { useGetRequest } = useRequest();

    const { search, ordering, group_type, service_provider } =
        Route.useSearch();

    const navigate = Route.useNavigate();

    const orgsQuery = useGetRequest<Organization[]>({
        url: "organizations/",
    });

    const serviceProviderOptions = useMemo(() => {
        const names = (orgsQuery.data ?? [])
            .map((o) => o.service_provider?.name)
            .filter((name): name is string => !!name);

        return Array.from(new Set(names)).sort();
    }, [orgsQuery.data]);

    const tableData = useMemo(() => {
        let data = orgsQuery.data ?? [];

        if (search?.trim()) {
            const q = search.trim().toLowerCase();
            data = data.filter(
                (o) =>
                    o.name?.toLowerCase().includes(q) ||
                    o.group_type?.toLowerCase().includes(q) ||
                    o.service_provider?.name?.toLowerCase().includes(q),
            );
        }

        if (group_type && group_type !== "all") {
            data = data.filter((o) => o.group_type === group_type);
        }

        if (service_provider && service_provider !== "all") {
            data = data.filter(
                (o) => o.service_provider?.name === service_provider,
            );
        }

        return data;
    }, [orgsQuery.data, search, group_type, service_provider]);

    const columns: ColumnDef<Organization>[] = useMemo(
        () => [
            {
                id: "name",
                header: "Name",
                accessorFn: (row) => row.name,
                cell: (info) => (
                    <div className="text-sm font-medium px-2">
                        {info.getValue() as string}
                    </div>
                ),
                size: 250,
            },
            {
                id: "group_type",
                header: "Type",
                accessorFn: (row) => row.group_type,
                cell: ({ row }) => {
                    const type = row.original.group_type;
                    const color =
                        type === "service provider" ? "primary" : "secondary";
                    return (
                        <Chip
                            size="sm"
                            radius="sm"
                            variant="flat"
                            color={color}
                        >
                            {formatGroupType(type)}
                        </Chip>
                    );
                },
                size: 120,
            },
            {
                id: "service_provider",
                header: "Service Provider",
                accessorFn: (row) => row.service_provider?.name ?? "",
                cell: (info) => (
                    <div className="text-sm px-2">
                        {info.getValue() as string}
                    </div>
                ),
                size: 200,
            },
            {
                id: "edit",
                enableSorting: false,
                header: "",
                accessorFn: (row) => row.id,
                cell: ({ row }) => (
                    <Button
                        as={Link}
                        to="/dashboard/network/organizations/$organizationId"
                        params={{
                            // @ts-expect-error ts(2322)
                            organizationId: row.original.id.toString(),
                        }}
                        // @ts-expect-error ts(2322)
                        search={(prev) => prev}
                        variant="flat"
                        size="sm"
                        startContent={<FontAwesomeIcon icon={faPenToSquare} />}
                    >
                        Edit
                    </Button>
                ),
                size: 90,
            },
        ],
        [],
    );

    const [sorting, setSorting] = useState<SortingState>([
        {
            id: ordering
                ? ordering.startsWith("-")
                    ? ordering.slice(1)
                    : ordering
                : "name",
            desc: ordering ? ordering.startsWith("-") : false,
        },
    ]);

    useEffect(() => {
        if (sorting.length > 0) {
            navigate({
                search: (prev) => ({
                    ...prev,
                    ordering: `${sorting[0].desc ? "-" : ""}${sorting[0].id}`,
                }),
            });
        } else {
            navigate({
                search: (prev) => ({ ...prev, ordering: "" }),
            });
        }
    }, [sorting, navigate]);

    return (
        <div className="h-full flex flex-col overflow-hidden px-4">
            <div className="flex flex-row gap-2 p-2 items-center justify-between w-full">
                <div className="flex flex-col lg:flex-row gap-2 items-center">
                    <Input
                        size="sm"
                        placeholder="Search..."
                        value={search ?? ""}
                        onValueChange={(value) => {
                            navigate({
                                search: (prev) => ({ ...prev, search: value }),
                            });
                        }}
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                        className="w-64"
                    />
                    <Select
                        size="sm"
                        aria-label="Type"
                        selectedKeys={[group_type ?? "all"]}
                        onChange={(e) =>
                            navigate({
                                search: (prev) => ({
                                    ...prev,
                                    group_type:
                                        e.target.value === "all"
                                            ? undefined
                                            : e.target.value,
                                }),
                            })
                        }
                        className="w-40"
                    >
                        <SelectItem key="all">All Types</SelectItem>
                        <SelectItem key="farm">Farm</SelectItem>
                        <SelectItem key="service provider">CSP</SelectItem>
                    </Select>
                    <Autocomplete
                        size="sm"
                        aria-label="Service Provider"
                        placeholder="Service Provider"
                        selectedKey={
                            service_provider && service_provider !== "all"
                                ? service_provider
                                : null
                        }
                        onSelectionChange={(key) => {
                            navigate({
                                search: (prev) => ({
                                    ...prev,
                                    service_provider:
                                        key == null ? undefined : String(key),
                                }),
                            });
                        }}
                        className="w-56"
                    >
                        {serviceProviderOptions.map((name) => (
                            <AutocompleteItem key={name} textValue={name}>
                                {name}
                            </AutocompleteItem>
                        ))}
                    </Autocomplete>
                </div>
                <div className="flex flex-row gap-2 items-center">
                    <Button
                        as={Link}
                        to="/dashboard/network/organizations/new"
                        color="primary"
                        size="sm"
                        startContent={<FontAwesomeIcon icon={faPlus} />}
                    >
                        Add Organization
                    </Button>
                </div>
            </div>
            <Divider />
            <TableV2
                data={tableData}
                columns={columns}
                sorting={sorting}
                setSorting={setSorting}
                isLoading={orgsQuery.isLoading}
                showScrollbar={true}
                onRowClick={(row) =>
                    navigate({
                        to: "/dashboard/network/organizations/$organizationId",
                        params: { organizationId: row.id.toString() },
                        search: (prev) => prev,
                    })
                }
            />
            <Outlet />
        </div>
    );
}
